import React, { useState } from "react";
import {
  Box, Text, Badge, Button, HStack, VStack, Icon, Circle, SimpleGrid, Select,
  Input, IconButton, useToast, Stat, StatLabel, StatNumber, FormControl, FormLabel,
  Flex, Tag, TagLabel, TagCloseButton, Link, Tooltip,
} from "@chakra-ui/react";
import {
  Phone, MessageSquare, Plus, Trash2, Pencil, Check, X, User, ClipboardList, Package, Activity,
  Car, Tag as TagIcon, ShoppingBag, Sparkles, ExternalLink, PauseCircle,
} from "lucide-react";
import {
  atualizarCrmCarteira, addCrmCarteira, addCrmPessoa, removerCrmPessoa, editarCrmPessoa,
  criarCrmContato, criarCrmTarefa, addCrmVeiculo, removerCrmVeiculo, removerCrmVeiculoConta, addCrmTag, removerCrmTag,
} from "../../services/api";
import { useSoftphone } from "../../contexts/softphone";
import {
  formatPhone, paraDiscagem, formatBRL, formatDate, formatDateTime, soDigitos,
  saudeInfo, recenciaLabel, rotuloResultado, corResultado, SEGMENTOS,
} from "./crmUtils";
import RegistrarAtividadeModal from "./RegistrarAtividadeModal";

const ACCENT = "#F68633";

/**
 * Ficha do cliente UNIFICADA — usada em Minha carteira, Base de contatos e na
 * aba Ficha das Conversas. Recebe o contrato montado pelo backend (montarFicha).
 * Sem cliente vinculado (chat), as ações de conta/tarefa/pessoa ficam inativas.
 */
export default function FichaCliente({ ficha, onReload, onAbrirChat, onChanged, onClose, emChat = false }) {
  const [salvando, setSalvando] = useState(false);
  const [abrindo, setAbrindo] = useState(null);
  const [ativOpen, setAtivOpen] = useState(false);
  const [novaPessoa, setNovaPessoa] = useState({ nome: "", telefone: "", cargo: "" });
  const [editId, setEditId] = useState(null);
  const [pessoaEdit, setPessoaEdit] = useState({ nome: "", telefone: "", cargo: "" });
  const [novaTarefa, setNovaTarefa] = useState({ motivo: "", prazo: "" });
  const [veic, setVeic] = useState({ marca: "", modelo: "", ano: "" });
  const [novaTag, setNovaTag] = useState("");
  const sp = useSoftphone();
  const toast = useToast();

  const conta = ficha.conta;
  const rfm = ficha.rfm;
  const clienteId = conta.cliente_id; // null quando o contato do chat não casa com cliente
  const s = saudeInfo(rfm?.saude);

  const recarregar = () => { onReload?.(); onChanged?.(); };

  const salvarConta = async (patch) => {
    if (!clienteId) return;
    setSalvando(true);
    const r = conta.em_carteira
      ? await atualizarCrmCarteira(clienteId, patch)
      : await addCrmCarteira({ cliente_id: clienteId, ...patch });
    setSalvando(false);
    if (r?.data) { toast({ status: "success", title: "Conta atualizada" }); recarregar(); }
    else toast({ status: "error", title: "Erro", description: r?.error });
  };

  // ── Ações por CONTATO (não pela conta) ──
  const ligar = (pessoa) => {
    const tel = pessoa?.telefone || conta.telefone;
    if (soDigitos(tel).length < 10) { toast({ status: "warning", title: "Sem telefone válido" }); return; }
    if (!sp.registered) { toast({ status: "warning", title: "Ramal desconectado", description: "Conecte o softphone no Discador." }); return; }
    sp.dial(paraDiscagem(tel));
    toast({ status: "info", title: "Discando...", description: formatPhone(tel) });
  };

  const conversar = async (pessoa) => {
    const tel = pessoa?.telefone || conta.telefone;
    if (soDigitos(tel).length < 10) { toast({ status: "warning", title: "Sem telefone válido" }); return; }
    setAbrindo(pessoa?.id ?? "conta");
    const r = await criarCrmContato({ telefone: tel, nome: pessoa?.nome || conta.nome, origem: "carteira" });
    setAbrindo(null);
    if (r?.data?.id) { onAbrirChat?.(r.data.id); onClose?.(); }
    else toast({ status: "error", title: "Erro", description: r?.error });
  };

  const addPessoa = async () => {
    if (!clienteId || !novaPessoa.nome.trim()) return;
    const r = await addCrmPessoa(clienteId, novaPessoa);
    if (r?.data) { setNovaPessoa({ nome: "", telefone: "", cargo: "" }); recarregar(); }
    else toast({ status: "error", title: "Erro", description: r?.error });
  };
  const delPessoa = async (id) => { if (id && clienteId) { await removerCrmPessoa(clienteId, id); recarregar(); } };
  const startEdit = (p) => { setEditId(p.id ?? "__virtual__"); setPessoaEdit({ nome: p.nome || "", telefone: p.telefone || "", cargo: p.cargo || "" }); };
  const salvarEdit = async () => {
    if (!clienteId || !pessoaEdit.nome.trim()) return;
    const novo = editId === "__virtual__"; // persiste o contato padrão (virtual) na própria linha
    const r = novo
      ? await addCrmPessoa(clienteId, { ...pessoaEdit, principal: true })
      : await editarCrmPessoa(clienteId, editId, pessoaEdit);
    if (r?.data) { setEditId(null); toast({ status: "success", title: novo ? "Contato salvo" : "Contato atualizado" }); recarregar(); }
    else toast({ status: "error", title: "Erro", description: r?.error });
  };
  // Confirma o contato padrão (virtual) como contato real em 1 clique, sem abrir linha extra.
  const confirmarPadrao = async (p) => {
    if (!clienteId || !(p.nome || p.telefone)) return;
    const r = await addCrmPessoa(clienteId, { nome: p.nome || "Contato", telefone: p.telefone || "", cargo: p.cargo || "", principal: true });
    if (r?.data) { toast({ status: "success", title: "Contato padrão salvo" }); recarregar(); }
    else toast({ status: "error", title: "Erro", description: r?.error });
  };

  const addTarefa = async () => {
    if (!clienteId || !novaTarefa.motivo.trim()) return;
    const r = await criarCrmTarefa({ cliente_id: clienteId, motivo: novaTarefa.motivo, prazo: novaTarefa.prazo || null });
    if (r?.data) { setNovaTarefa({ motivo: "", prazo: "" }); toast({ status: "success", title: "Tarefa criada" }); recarregar(); }
    else toast({ status: "error", title: "Erro", description: r?.error });
  };

  // Veículos/tags vivem no contato de chat (crm_contatos). Sem um, cria pelo telefone da conta.
  const garantirContatoChat = async () => {
    if (ficha.crm_contato_id) return ficha.crm_contato_id;
    const tel = conta.telefone || ficha.pessoas?.find((p) => p.telefone)?.telefone;
    if (soDigitos(tel).length < 10) { toast({ status: "warning", title: "Sem telefone para vincular" }); return null; }
    const r = await criarCrmContato({ telefone: tel, nome: conta.nome, origem: "carteira" });
    return r?.data?.id ?? null;
  };
  const addVeic = async () => {
    if (!veic.marca && !veic.modelo) { toast({ status: "warning", title: "Informe marca ou modelo" }); return; }
    const cid = await garantirContatoChat();
    if (!cid) return;
    const r = await addCrmVeiculo(cid, veic);
    if (r?.data) { setVeic({ marca: "", modelo: "", ano: "" }); recarregar(); }
    else toast({ status: "error", title: "Erro", description: r?.error });
  };
  // Veículo informado numa compra do site não tem contato de chat: apaga pela conta.
  const delVeic = async (v) => {
    if (v.crm_contato_id) await removerCrmVeiculo(v.crm_contato_id, v.id);
    else if (clienteId) await removerCrmVeiculoConta(clienteId, v.id);
    recarregar();
  };
  const addTag = async () => {
    if (!novaTag.trim()) return;
    const cid = await garantirContatoChat();
    if (!cid) return;
    const r = await addCrmTag(cid, novaTag.trim());
    if (r?.data) { setNovaTag(""); recarregar(); }
    else toast({ status: "error", title: "Erro", description: r?.error });
  };
  const delTag = async (t) => { await removerCrmTag(t.crm_contato_id, t.id); recarregar(); };

  const pessoas = ficha.pessoas || [];

  return (
    <>
      {/* Barra de ações de conta (registrar atividade) */}
      {clienteId && (
        <HStack mb={4} flexWrap="wrap">
          <Button size="sm" leftIcon={<Activity size={15} />} variant="outline" onClick={() => setAtivOpen(true)}>Registrar atividade</Button>
          {!conta.em_carteira && (
            <Button size="sm" colorScheme="orange" leftIcon={<Plus size={15} />} onClick={() => salvarConta({})} isLoading={salvando}>Adicionar à minha carteira</Button>
          )}
          {/* Cooldown (V1.7): sem o selo, a conta simplesmente para de receber
              tarefa automática e ninguém sabe por quê. */}
          {conta.cooldown_ate && (
            <Tooltip label="Desfecho de cliente perdido ou tentativas de contato esgotadas. Enquanto durar, o sistema não gera tarefa proativa para esta conta — mas você pode criar uma à mão.">
              <Badge
                colorScheme="purple" variant="subtle" borderRadius="full"
                px={3} py={1} display="flex" alignItems="center" gap={1} cursor="help"
              >
                <Icon as={PauseCircle} boxSize={3.5} />
                Em descanso até {formatDate(conta.cooldown_ate)}
              </Badge>
            </Tooltip>
          )}
        </HStack>
      )}
      {!clienteId && (
        <Box mb={4} bg="orange.50" borderRadius="md" px={3} py={2}>
          <Text fontSize="xs" color="orange.700">Contato não vinculado a um cliente do sistema. Histórico de compras, tarefas e atividades ficam indisponíveis.</Text>
        </Box>
      )}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
        {/* ── Coluna esquerda ── */}
        <VStack align="stretch" spacing={4}>
          {/* Saúde RFM */}
          <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
            <HStack mb={3}>
              <Circle size="12px" bg={s.hex} />
              <Text fontWeight="bold" fontSize="sm">{s.label}</Text>
              <Text fontSize="xs" color="gray.400">· últimos {rfm.periodo_dias} dias</Text>
            </HStack>
            <SimpleGrid columns={3} spacing={2}>
              <Stat><StatLabel fontSize="xs">Recência</StatLabel><StatNumber fontSize="sm">{recenciaLabel(rfm.recencia_dias)}</StatNumber></Stat>
              <Stat><StatLabel fontSize="xs">Frequência</StatLabel><StatNumber fontSize="md">{rfm.frequencia}</StatNumber></Stat>
              <Stat><StatLabel fontSize="xs">Valor</StatLabel><StatNumber fontSize="md">{formatBRL(rfm.valor_periodo)}</StatNumber></Stat>
            </SimpleGrid>
          </Box>

          {/* Cashback */}
          {clienteId && (
            <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
              <Text fontWeight="bold" fontSize="sm" mb={3}>Cashback</Text>
              <SimpleGrid columns={3} spacing={2}>
                <Stat><StatLabel fontSize="xs">Disponível</StatLabel><StatNumber fontSize="md" color="green.500">{formatBRL(ficha.cashback?.disponivel || 0)}</StatNumber></Stat>
                <Stat><StatLabel fontSize="xs">Pendente</StatLabel><StatNumber fontSize="md">{formatBRL(ficha.cashback?.pendente || 0)}</StatNumber></Stat>
                <Stat><StatLabel fontSize="xs">Utilizado</StatLabel><StatNumber fontSize="md" color="gray.500">{formatBRL(ficha.cashback?.utilizado || 0)}</StatNumber></Stat>
              </SimpleGrid>
            </Box>
          )}

          {/* Conta */}
          <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
            <Text fontWeight="bold" fontSize="sm" mb={3}>Conta</Text>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>Segmento</FormLabel>
                <Select size="sm" placeholder="Não definido" value={conta.tipo_segmento || ""} onChange={(e) => salvarConta({ tipo_segmento: e.target.value })} isDisabled={!clienteId || salvando}>
                  {SEGMENTOS.map((seg) => <option key={seg.value} value={seg.value}>{seg.label}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>Status</FormLabel>
                <Select size="sm" value={conta.status || "ativo"} onChange={(e) => salvarConta({ status: e.target.value })} isDisabled={!clienteId || !conta.em_carteira || salvando}>
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </Select>
              </FormControl>
              {conta.em_carteira && (
                <Box>
                  <FormLabel fontSize="xs" mb={1}>Vendedor responsável</FormLabel>
                  <Text fontSize="sm">{conta.vendedor_nome || "Sem dono"}</Text>
                </Box>
              )}
              {conta.email && (
                <Box>
                  <FormLabel fontSize="xs" mb={1}>E-mail</FormLabel>
                  <Text fontSize="sm">{conta.email}</Text>
                </Box>
              )}
              {conta.endereco?.resumo && (
                <Box>
                  <FormLabel fontSize="xs" mb={1}>Endereço</FormLabel>
                  <Text fontSize="sm">{conta.endereco.resumo}{conta.endereco.cep ? ` · ${conta.endereco.cep}` : ""}</Text>
                </Box>
              )}
            </VStack>
          </Box>

          {/* Contatos (ações por contato) */}
          <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
            <HStack mb={3}><Icon as={User} boxSize={4} color={ACCENT} /><Text fontWeight="bold" fontSize="sm">Contatos</Text></HStack>
            <VStack align="stretch" spacing={2} mb={3}>
              {pessoas.length === 0 && <Text fontSize="xs" color="gray.400">Nenhum contato cadastrado.</Text>}
              {pessoas.map((p, i) => (
                editId === (p.id ?? "__virtual__") ? (
                  <HStack key={p.id ?? `e${i}`} spacing={1}>
                    <Input size="xs" placeholder="Nome" value={pessoaEdit.nome} onChange={(e) => setPessoaEdit((x) => ({ ...x, nome: e.target.value }))} />
                    <Input size="xs" placeholder="Cargo" value={pessoaEdit.cargo} onChange={(e) => setPessoaEdit((x) => ({ ...x, cargo: e.target.value }))} />
                    <Input size="xs" placeholder="Telefone" value={pessoaEdit.telefone} onChange={(e) => setPessoaEdit((x) => ({ ...x, telefone: e.target.value }))} />
                    <IconButton aria-label="Salvar" size="xs" colorScheme="green" icon={<Check size={14} />} onClick={salvarEdit} />
                    <IconButton aria-label="Cancelar" size="xs" variant="ghost" icon={<X size={14} />} onClick={() => setEditId(null)} />
                  </HStack>
                ) : (
                  <Flex key={p.id ?? `v${i}`} justify="space-between" align="center" gap={2}>
                    <Box minW={0}>
                      <Text fontSize="sm" noOfLines={1}>
                        {p.nome}{p.cargo ? <Text as="span" color="gray.400" fontSize="xs"> · {p.cargo}</Text> : null}
                        {p.virtual && <Badge ml={1} fontSize="9px" colorScheme="gray">padrão</Badge>}
                      </Text>
                      {p.telefone && <Text fontSize="xs" color="gray.400">{formatPhone(p.telefone)}</Text>}
                    </Box>
                    <HStack spacing={1} flexShrink={0}>
                      <IconButton aria-label="Ligar" size="xs" variant="ghost" colorScheme="green" icon={<Phone size={14} />} onClick={() => ligar(p)} />
                      {!emChat && <IconButton aria-label="Conversar" size="xs" variant="ghost" colorScheme="orange" icon={<MessageSquare size={14} />} isLoading={abrindo === (p.id ?? "conta")} onClick={() => conversar(p)} />}
                      {/* Contato padrão (virtual): salvar em 1 clique na própria linha, ou editar antes. */}
                      {p.virtual && clienteId && <IconButton aria-label="Salvar contato padrão" title="Salvar contato padrão" size="xs" variant="ghost" colorScheme="green" icon={<Check size={14} />} onClick={() => confirmarPadrao(p)} />}
                      {clienteId && <IconButton aria-label="Editar" size="xs" variant="ghost" colorScheme="blue" icon={<Pencil size={14} />} onClick={() => startEdit(p)} />}
                      {!p.virtual && clienteId && <IconButton aria-label="Remover" size="xs" variant="ghost" colorScheme="red" icon={<Trash2 size={14} />} onClick={() => delPessoa(p.id)} />}
                    </HStack>
                  </Flex>
                )
              ))}
            </VStack>
            {clienteId && (
              <HStack>
                <Input size="xs" placeholder="Nome" value={novaPessoa.nome} onChange={(e) => setNovaPessoa((x) => ({ ...x, nome: e.target.value }))} />
                <Input size="xs" placeholder="Cargo" value={novaPessoa.cargo} onChange={(e) => setNovaPessoa((x) => ({ ...x, cargo: e.target.value }))} />
                <Input size="xs" placeholder="Telefone" value={novaPessoa.telefone} onChange={(e) => setNovaPessoa((x) => ({ ...x, telefone: e.target.value }))} />
                <IconButton aria-label="Adicionar contato" size="xs" colorScheme="orange" icon={<Plus size={14} />} onClick={addPessoa} />
              </HStack>
            )}
          </Box>

          {/* Veículos */}
          <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
            <HStack mb={3}><Icon as={Car} boxSize={4} color={ACCENT} /><Text fontWeight="bold" fontSize="sm">Veículos</Text></HStack>
            <VStack align="stretch" spacing={2} mb={3}>
              {(ficha.veiculos || []).length === 0 && <Text fontSize="xs" color="gray.400">Nenhum veículo.</Text>}
              {(ficha.veiculos || []).map((v) => (
                <Flex key={v.id} bg="gray.50" borderRadius="md" px={3} py={1.5} align="center">
                  <Text fontSize="sm" flex="1" noOfLines={1}>{[v.marca, v.modelo, v.ano].filter(Boolean).join(" · ")}</Text>
                  <IconButton aria-label="Remover" size="xs" variant="ghost" icon={<Trash2 size={13} />} onClick={() => delVeic(v)} />
                </Flex>
              ))}
            </VStack>
            <HStack>
              <Input size="xs" placeholder="Marca" value={veic.marca} onChange={(e) => setVeic((x) => ({ ...x, marca: e.target.value }))} />
              <Input size="xs" placeholder="Modelo" value={veic.modelo} onChange={(e) => setVeic((x) => ({ ...x, modelo: e.target.value }))} />
              <Input size="xs" placeholder="Ano" w="70px" value={veic.ano} onChange={(e) => setVeic((x) => ({ ...x, ano: e.target.value }))} />
              <IconButton aria-label="Adicionar veículo" size="xs" colorScheme="orange" icon={<Plus size={14} />} onClick={addVeic} />
            </HStack>
          </Box>

          {/* Peças de interesse / tags */}
          <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
            <HStack mb={3}><Icon as={TagIcon} boxSize={4} color={ACCENT} /><Text fontWeight="bold" fontSize="sm">Peças de interesse / tags</Text></HStack>
            <Flex gap={2} flexWrap="wrap" mb={3}>
              {(ficha.tags || []).length === 0 && (ficha.interesses || []).length === 0 && <Text fontSize="xs" color="gray.400">Nenhuma peça/tag.</Text>}
              {(ficha.tags || []).map((t) => (
                <Tag key={`tag-${t.id}`} colorScheme="orange" borderRadius="full">
                  <TagLabel>{t.tag}</TagLabel>
                  <TagCloseButton onClick={() => delTag(t)} />
                </Tag>
              ))}
              {/* Peças procuradas identificadas pelo bot — read-only (variant subtle) */}
              {(ficha.interesses || []).map((i) => (
                <Tag key={`int-${i.id}`} colorScheme="orange" variant="subtle" borderRadius="full" title="Identificada pelo bot">
                  <TagLabel>{i.termo}</TagLabel>
                </Tag>
              ))}
            </Flex>
            <HStack>
              <Input size="xs" placeholder="Ex.: pastilha de freio, Gol G5..." value={novaTag} onChange={(e) => setNovaTag(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTag()} />
              <IconButton aria-label="Adicionar tag" size="xs" colorScheme="orange" icon={<Plus size={14} />} onClick={addTag} />
            </HStack>
          </Box>
        </VStack>

        {/* ── Coluna direita ── */}
        <VStack align="stretch" spacing={4}>
          {/* Últimos pedidos (com status) — total de pedidos e valor total (só pagamento confirmado) */}
          <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
            <Flex mb={3} justify="space-between" align="center" gap={2} flexWrap="wrap">
              <HStack><Icon as={Package} boxSize={4} color={ACCENT} /><Text fontWeight="bold" fontSize="sm">Últimos pedidos</Text></HStack>
              {ficha.pedidos_resumo?.total_pedidos > 0 && (
                <HStack spacing={2} fontSize="xs">
                  <Badge colorScheme="orange" borderRadius="full">{ficha.pedidos_resumo.total_pedidos} pedido(s)</Badge>
                  <Text fontWeight="bold" color="gray.700">{formatBRL(ficha.pedidos_resumo.valor_total)}</Text>
                </HStack>
              )}
            </Flex>
            {(ficha.pedidos || []).length === 0 ? (
              <Text fontSize="xs" color="gray.400">Nenhum pedido.</Text>
            ) : (
              <VStack align="stretch" spacing={0} maxH="200px" overflowY="auto">
                {ficha.pedidos.map((p) => (
                  <Link key={p.id} href={`/vendas?busca=${p.id}`} isExternal _hover={{ textDecoration: "none", bg: "gray.50" }} borderRadius="md" title={`Abrir venda #${p.id} no Admin`}>
                    <HStack justify="space-between" fontSize="xs" py={1.5} px={1} borderBottomWidth="1px" borderColor="gray.50">
                      <HStack spacing={2} minW={0}>
                        <Badge variant="subtle" colorScheme={p.status?.cor || "gray"}>{p.status?.label || "—"}</Badge>
                        <Text color="gray.500" noOfLines={1}>{p.canal} · {formatDate(p.data)}</Text>
                      </HStack>
                      <HStack spacing={2} flexShrink={0}>
                        <Text color="gray.400">{p.itens} item(s)</Text>
                        <Text fontWeight="medium">{formatBRL(p.valor)}</Text>
                        <Icon as={ExternalLink} boxSize={3} color="gray.400" />
                      </HStack>
                    </HStack>
                  </Link>
                ))}
              </VStack>
            )}
            {ficha.pedidos_resumo?.total_pedidos > 0 && (
              <Text fontSize="10px" color="gray.400" mt={2}>Total considera apenas pedidos com pagamento confirmado.</Text>
            )}
          </Box>

          {/* Já comprou (peças) */}
          {(ficha.pecas || []).length > 0 && (
            <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
              <HStack mb={3}><Icon as={ShoppingBag} boxSize={4} color={ACCENT} /><Text fontWeight="bold" fontSize="sm">Já comprou</Text></HStack>
              <VStack align="stretch" spacing={1} maxH="160px" overflowY="auto">
                {ficha.pecas.map((p, i) => (
                  <Flex key={i} fontSize="xs" justify="space-between" align="center" bg="gray.50" px={2} py={1} borderRadius="md" gap={2}>
                    <Box minW={0} flex="1">
                      <Text noOfLines={1}>{p.produto || p.codigo_global || "Item"}{p.quantidade ? ` ×${p.quantidade}` : ""}</Text>
                      {p.codigo_global && <Text color="gray.400" fontSize="10px">SKU {p.codigo_global}</Text>}
                    </Box>
                    <HStack spacing={2} flexShrink={0}>
                      <Text color="gray.400">{formatDate(p.data_venda)}</Text>
                      {p.produto_id && (
                        <Link href={`/produtos?id=${p.produto_id}`} isExternal title="Ver na base de produtos" color={ACCENT} display="inline-flex">
                          <Icon as={ExternalLink} boxSize={3.5} />
                        </Link>
                      )}
                    </HStack>
                  </Flex>
                ))}
              </VStack>
            </Box>
          )}

          {/* Tarefas abertas */}
          <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
            <HStack mb={3}><Icon as={ClipboardList} boxSize={4} color={ACCENT} /><Text fontWeight="bold" fontSize="sm">Tarefas abertas</Text></HStack>
            <VStack align="stretch" spacing={1} mb={3}>
              {(ficha.tarefas || []).filter((t) => t.status !== "feita").length === 0 && <Text fontSize="xs" color="gray.400">Sem tarefas abertas.</Text>}
              {(ficha.tarefas || []).filter((t) => t.status !== "feita").map((t) => (
                <HStack key={t.id} justify="space-between" fontSize="xs">
                  <Text noOfLines={1}>{t.motivo}</Text>
                  {t.prazo && <Text color="gray.400" flexShrink={0}>{formatDate(t.prazo)}</Text>}
                </HStack>
              ))}
            </VStack>
            {clienteId && (
              <HStack>
                <Input size="xs" placeholder="Nova tarefa..." value={novaTarefa.motivo} onChange={(e) => setNovaTarefa((x) => ({ ...x, motivo: e.target.value }))} />
                <Input size="xs" type="date" w="150px" value={novaTarefa.prazo} onChange={(e) => setNovaTarefa((x) => ({ ...x, prazo: e.target.value }))} />
                <IconButton aria-label="Criar tarefa" size="xs" colorScheme="orange" icon={<Plus size={14} />} onClick={addTarefa} />
              </HStack>
            )}
          </Box>

          {/* Histórico de atividades */}
          <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
            <HStack mb={3}><Icon as={Activity} boxSize={4} color={ACCENT} /><Text fontWeight="bold" fontSize="sm">Histórico de atividades</Text></HStack>
            {(ficha.atividades || []).length === 0 ? (
              <Text fontSize="xs" color="gray.400">Nenhuma atividade registrada.</Text>
            ) : (
              <VStack align="stretch" spacing={2} maxH="200px" overflowY="auto">
                {ficha.atividades.map((a) => (
                  <Box key={a.id} fontSize="xs" borderLeftWidth="2px" borderColor={`${corResultado(a.resultado)}.300`} pl={2}>
                    <HStack justify="space-between">
                      <HStack spacing={2}>
                        <Badge colorScheme={a.tipo === "ligacao" ? "purple" : "green"}>{a.tipo === "ligacao" ? "Ligação" : "WhatsApp"}</Badge>
                        {a.resultado && <Badge colorScheme={corResultado(a.resultado)} variant="subtle">{rotuloResultado(a.resultado)}</Badge>}
                      </HStack>
                      <Text color="gray.400">{formatDateTime(a.data)}</Text>
                    </HStack>
                    {a.resumo && <Text color="gray.600" mt={1}>{a.resumo}</Text>}
                    {a.vendedor?.name && <Text color="gray.400" mt={0.5}>— {a.vendedor.name}</Text>}
                  </Box>
                ))}
              </VStack>
            )}
          </Box>

          {/* Resumo do atendimento (bot) */}
          {conta.resumo_bot && (
            <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
              <HStack mb={3}>
                <Icon as={Sparkles} boxSize={4} color={ACCENT} />
                <Text fontWeight="bold" fontSize="sm">Resumo do atendimento (bot)</Text>
                {conta.resumo_bot_em && <Text fontSize="xs" color="gray.400">· {formatDate(conta.resumo_bot_em)}</Text>}
              </HStack>
              <Box bg="orange.50" borderRadius="md" px={3} py={2} borderLeftWidth="3px" borderColor={ACCENT}>
                <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">{conta.resumo_bot}</Text>
              </Box>
            </Box>
          )}

          {/* Ligações */}
          {(ficha.ligacoes || []).length > 0 && (
            <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
              <HStack mb={3}><Icon as={Phone} boxSize={4} color={ACCENT} /><Text fontWeight="bold" fontSize="sm">Ligações ({ficha.total_ligacoes})</Text></HStack>
              <VStack align="stretch" spacing={1} maxH="160px" overflowY="auto">
                {ficha.ligacoes.map((l) => (
                  <Flex key={l.id} fontSize="xs" justify="space-between" bg="gray.50" px={2} py={1} borderRadius="md">
                    <HStack spacing={1}>
                      <Badge colorScheme={l.direcao === "saida" ? "blue" : "green"}>{l.direcao === "saida" ? "saída" : "entrada"}</Badge>
                      <Text>{l.status}</Text>
                    </HStack>
                    <Text color="gray.400">{formatDateTime(l.iniciada_em)}</Text>
                  </Flex>
                ))}
              </VStack>
            </Box>
          )}
        </VStack>
      </SimpleGrid>

      {clienteId && (
        <RegistrarAtividadeModal
          isOpen={ativOpen}
          onClose={() => setAtivOpen(false)}
          clienteId={clienteId}
          clienteNome={conta?.nome}
          onSaved={recarregar}
        />
      )}
    </>
  );
}
